import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Text, Badge, Box } from "@mantine/core";
import { IconPencil, IconTrash } from "@tabler/icons";
import DataTable from "./dataTable";
import ActionCellRender from "./actionCellRender";
import { useFormRender } from "../hooks/useFormRender";
import {
  FUND_FILTER_COLUMNS,
  buildFundDistinctValues,
  buildTableFilterChange,
  filterFunds,
  paginateFunds,
  sortFunds,
} from "../helpers/approvalFundListFundsUtils";
import {
  attachSourceIndexes,
  buildFundEditFormSchema,
  formatFundAmount,
  stripInternalFundFields,
} from "../helpers/approvalFundListFundFields";

const yesNoBadge = (value) => {
  if (value === true) {
    return <Badge color="green">Yes</Badge>;
  }
  if (value === false) {
    return <Badge color="gray">No</Badge>;
  }
  return "--";
};

const booleanColumn = (field, headerName, minWidth = 120) => ({
  field,
  headerName,
  sortable: true,
  minWidth,
  cellRender: (_col, data) => yesNoBadge(data[field]),
});

const amountColumn = (field, headerName, minWidth = 180) => ({
  field,
  headerName,
  sortable: true,
  minWidth,
  cellRender: (_col, data) => formatFundAmount(data[field]),
});

const FUND_COLUMNS = [
  { field: "isinCode", headerName: "ISIN Code", sortable: true, minWidth: 140 },
  { field: "fundName", headerName: "Name of the fund", sortable: true, minWidth: 280 },
  { field: "ifastCode", headerName: "iFAST Code", sortable: true, minWidth: 120 },
  {
    field: "investmentHorizon",
    headerName: "Investment Horizon",
    sortable: true,
    minWidth: 150,
  },
  { field: "riskRating", headerName: "Risk Rating", sortable: true, minWidth: 110 },
  { field: "trailerRebate", headerName: "Trailer Rebate", sortable: true, minWidth: 120 },
  { field: "assetClass", headerName: "Asset Class", sortable: true, minWidth: 130 },
  booleanColumn("highYieldBondFund", "High Yield Bond Fund", 160),
  { field: "fundCurrency", headerName: "Fund Currency", sortable: true, minWidth: 120 },
  amountColumn("minSubscriptionAmount", "Min Subscription Amount"),
  amountColumn("minSubsequentInvestment", "Minimum Subsequent Investment", 220),
  amountColumn("minHolding", "Minimum Holding", 150),
  amountColumn("minRedemptionAmount", "Minimum Redemption Amount", 200),
  {
    field: "dividendFrequency",
    headerName: "Dividend Frequency",
    sortable: true,
    minWidth: 180,
  },
  booleanColumn("complexProduct", "Complex Product?", 140),
  booleanColumn("derivativesFund", "Derivatives Fund?", 150),
  booleanColumn("cies", "CIES?", 90),
  booleanColumn("newCies", "New CIES?", 110),
  booleanColumn("buyEnabledIfastCentral", "Buy Enabled - iFAST Central", 200),
  booleanColumn("buyEnabledIgp", "Buy Enabled - iGP", 150),
  booleanColumn("rspAvailability", "RSP Availability", 140),
  {
    field: "action",
    headerName: "Action",
    isAction: true,
    minWidth: 90,
    cellRender: ActionCellRender,
    actions: [
      { name: "edit", label: "Edit", icon: IconPencil },
      { name: "delete", label: "Delete", icon: IconTrash },
    ],
  },
];

const TABLE_COLUMNS = [...FUND_COLUMNS, ...FUND_FILTER_COLUMNS];

const ApprovalFundListFunds = ({ form }) => {
  const funds = form?.values?.funds ?? [];
  const [searchText, setSearchText] = useState("");
  const [tableFilterQuery, setTableFilterQuery] = useState({});
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(50);
  const [sort, setSort] = useState({ by: "fundName", order: "asc" });
  const [selectedSourceIndex, setSelectedSourceIndex] = useState(-1);

  const fundEditFormSchema = useMemo(
    () => ({
      ...buildFundEditFormSchema(),
      closeAfterSave: true,
    }),
    []
  );

  const persistFunds = useCallback(
    async (updatedFunds) => {
      await form.updateAndSave({ funds: updatedFunds });
    },
    [form]
  );

  const onFundFormSubmit = useCallback(
    async ({ values, formMode }) => {
      if (formMode !== "edit" || selectedSourceIndex < 0) return;

      const updatedFunds = [...funds];
      updatedFunds[selectedSourceIndex] = stripInternalFundFields(values);
      await persistFunds(updatedFunds);
      setSelectedSourceIndex(-1);
    },
    [funds, persistFunds, selectedSourceIndex]
  );

  const onFundFormSubmitSuccess = useCallback(() => {
    setSelectedSourceIndex(-1);
  }, []);

  const [renderFundForm, fundFormAction] = useFormRender(
    { edit: fundEditFormSchema },
    onFundFormSubmitSuccess,
    onFundFormSubmit
  );

  const indexedFunds = useMemo(() => attachSourceIndexes(funds), [funds]);

  const buildDistinct = useCallback(
    (dataSource, col) => buildFundDistinctValues(dataSource, col),
    []
  );

  const onTableFilterChange = useMemo(
    () =>
      buildTableFilterChange({
        setTableFilterQuery,
        setCurrentPage,
      }),
    []
  );

  const filteredFunds = useMemo(
    () => filterFunds(indexedFunds, { searchText, tableFilterQuery }),
    [indexedFunds, searchText, tableFilterQuery]
  );

  const sortedFunds = useMemo(
    () => sortFunds(filteredFunds, sort),
    [filteredFunds, sort]
  );

  const { docs: pageFunds, total, totalPage, page } = useMemo(
    () => paginateFunds(sortedFunds, { currentPage, pageSize }),
    [sortedFunds, currentPage, pageSize]
  );

  useEffect(() => {
    if (currentPage > totalPage) {
      setCurrentPage(totalPage);
    }
  }, [currentPage, totalPage]);

  const pagination = useMemo(
    () => ({
      total,
      totalPage,
      page,
    }),
    [total, totalPage, page]
  );

  const onSearchChange = useCallback((text) => {
    setCurrentPage(1);
    setSearchText(text);
  }, []);

  const onPageChange = useCallback((pageNumber) => {
    setCurrentPage(pageNumber);
  }, []);

  const onPageSizeChange = useCallback((size) => {
    setPageSize(Number(size));
    setCurrentPage(1);
  }, []);

  const onSortChange = useCallback((by, reverseSortDirection) => {
    if (!by) return;
    setSort({ by, order: reverseSortDirection ? "desc" : "asc" });
  }, []);

  const openFundEdit = useCallback(
    (data) => {
      const sourceIndex = data?.__sourceIndex;
      if (sourceIndex == null || sourceIndex < 0) return;

      setSelectedSourceIndex(sourceIndex);
      fundFormAction.open({ mode: "edit", data });
    },
    [fundFormAction]
  );

  const handleActionBtnClick = useCallback(
    async ({ action, data }) => {
      const sourceIndex = data?.__sourceIndex;
      if (sourceIndex == null || sourceIndex < 0) return;

      if (action === "edit") {
        openFundEdit(data);
        return;
      }

      if (action === "delete") {
        const label = data.fundName || data.ifastCode || data.isinCode || "this fund";
        if (!window.confirm(`Delete ${label}?`)) return;

        const updatedFunds = funds.filter((_, index) => index !== sourceIndex);
        await persistFunds(updatedFunds);
      }
    },
    [funds, openFundEdit, persistFunds]
  );

  const handleRowDoubleClick = useCallback(
    ({ data }) => openFundEdit(data),
    [openFundEdit]
  );

  if (!funds.length) {
    return (
      <Text size="sm" color="dimmed">
        No funds in this list.
      </Text>
    );
  }

  return (
    <>
      <Box sx={{ width: "100%", maxWidth: "100%" }}>
        <DataTable
          data={pageFunds}
          columns={TABLE_COLUMNS}
          hidePagination={false}
          hideSearchBar={false}
          horizontalScroll
          localFilterMode
          buildDistinct={buildDistinct}
          filterDataSource={indexedFunds}
          columnFilterLayout="toolbar"
          searchText={searchText}
          onSearchChange={onSearchChange}
          tableFilterQuery={tableFilterQuery}
          onFilterChange={onTableFilterChange}
          onSortChange={onSortChange}
          onActionBtnClick={handleActionBtnClick}
          onRowDoubleClick={handleRowDoubleClick}
          currentPage={page}
          pageSize={pageSize}
          pagination={pagination}
          onPageChange={onPageChange}
          onPageSizeChange={onPageSizeChange}
        />
      </Box>
      {renderFundForm()}
    </>
  );
};

export default ApprovalFundListFunds;
